import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberPartnerPersonal.module.scss"; // reuse same styles
import api from "@/utils/axios";
import { toast } from "react-toastify";

import {
  GENDERS,
  EDUCATION_LEVELS,
  LOYALTY_LEVELS,
  FINANCIAL_LEVELS,
  PRIORITY_LEVELS,
  WORK_WITH_US_ROLES,
  COUNTRIES,
} from "@/enums/MemberEnums";

const UserMemberPartnerPersonal = () => {
  const { userId, partnerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [personal, setPersonal] = useState(null);

  // Real image
  const [realImageFile, setRealImageFile] = useState(null);
  const [realImagePreview, setRealImagePreview] = useState(null);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [workPlaceName, setWorkPlaceName] = useState("");
  const [workPosition, setWorkPosition] = useState("");
  const [responsibleNotes, setResponsibleNotes] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [schoolNote, setSchoolNote] = useState("");
  const [universityNote, setUniversityNote] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialTikTok, setSocialTikTok] = useState("");
  const [socialYouTube, setSocialYouTube] = useState("");
  const [loyalty, setLoyalty] = useState("");
  const [financial, setFinancial] = useState("");
  const [priority, setPriority] = useState("");
  const [workWithUs, setWorkWithUs] = useState("");

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const genderLabel = (g) => formatLabel(g);
  const countryLabel = (c) => c;

  const realImageUrl = useMemo(() => {
    if (realImagePreview) return realImagePreview;
    if (personal?.realImage?.url) return personal.realImage.url;
    return null;
  }, [realImagePreview, personal]);

  const loadPersonal = async () => {
    if (!partnerId) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/single/partners-info/personal-info/${partnerId}`
      );
      const data = res.data?.personal;
      setPersonal(data || null);

      if (data) {
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setBirthday(
          data.birthday
            ? new Date(data.birthday).toISOString().slice(0, 10)
            : ""
        );
        setGender(data.gender || "");
        setCountry(data.country || "");
        setCity(data.city || "");
        setAddress(data.address || "");
        setMobilePhone(data.mobilePhone || "");
        setWorkPlaceName(data.workPlaceName || "");
        setWorkPosition(data.workPosition || "");
        setResponsibleNotes(data.responsibleNotes || "");
        setEducationLevel(data.educationLevel || "");
        setSchoolNote(data.schoolNote || "");
        setUniversityNote(data.universityNote || "");
        setSocialFacebook(data.socialFacebook || "");
        setSocialInstagram(data.socialInstagram || "");
        setSocialTikTok(data.socialTikTok || "");
        setSocialYouTube(data.socialYouTube || "");
        setLoyalty(data.loyalty || "");
        setFinancial(data.financial || "");
        setPriority(data.priority || "");
        setWorkWithUs(data.work_with_us || "");
      }

      setRealImageFile(null);
      setRealImagePreview(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load partner personal info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPersonal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!personal) return;

    try {
      setSaving(true);
      await api.patch(
        `/v1/admin/members/single/partners-info/personal-info/${partnerId}`,
        {
          firstName: firstName || "",
          lastName: lastName || "",
          birthday: birthday || undefined,
          gender: gender || undefined,
          country: country || undefined,
          city: city || "",
          address: address || "",
          mobilePhone: mobilePhone || "",
          workPlaceName: workPlaceName || "",
          workPosition: workPosition || "",
          responsibleNotes: responsibleNotes || "",
          educationLevel: educationLevel || undefined,
          schoolNote: schoolNote || "",
          universityNote: universityNote || "",
          socialFacebook: socialFacebook || "",
          socialInstagram: socialInstagram || "",
          socialTikTok: socialTikTok || "",
          socialYouTube: socialYouTube || "",
          loyalty: loyalty || undefined,
          financial: financial || undefined,
          priority: priority || undefined,
          work_with_us: workWithUs || undefined,
        }
      );

      toast.success("Partner personal info updated");
      await loadPersonal();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        "Failed to update partner personal info";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRealImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setRealImageFile(null);
      setRealImagePreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setRealImageFile(file);
    const url = URL.createObjectURL(file);
    setRealImagePreview(url);
  };

  const handleUploadRealImage = async () => {
    if (!realImageFile) {
      toast.error("Please choose a real image first");
      return;
    }

    try {
      setUploading(true);

      const file = realImageFile;
      const ext =
        file.name && file.name.includes(".")
          ? file.name.split(".").pop().toLowerCase()
          : "webp";
      const contentType = file.type || "image/webp";

      // Get presigned URL for partner
      const res = await api.post(
        `/v1/admin/members/single/partners-info/real-image/presign/${userId}/${partnerId}`,
        { ext, contentType }
      );

      const { uploadUrl } = res.data || {};
      if (!uploadUrl) {
        toast.error("Presign failed: missing upload URL");
        return;
      }

      // Upload to S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: file,
      });

      if (!putRes.ok) {
        console.error("S3 upload failed", putRes.status, await putRes.text());
        toast.error("Failed to upload real image");
        return;
      }

      toast.success("Real image uploaded successfully");
      setRealImageFile(null);
      setRealImagePreview(null);
      await loadPersonal();
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload real image");
    } finally {
      setUploading(false);
    }
  };

  const handleViewImage = () => {
    const url = realImageUrl;
    if (!url) {
      toast.error("No real image available to view");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleBack = () => {
    // Go back to partner single profile
    navigate(
      `/admin/members/single/partners-info/single/${userId}/${partnerId}`
    );
  };

  if (loading && !personal) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Partner Personal Info</h1>
          <p className={cls.subtitle}>Loading partner personal profile…</p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  if (!personal) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Partner Personal Info</h1>
          <p className={cls.subtitle}>
            No personal profile found for this partner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>Partner Personal Profile</h1>
        <p className={cls.subtitle}>
          Private identity and lifestyle trust details for this partner. Admin
          only, never visible to the public profile.
        </p>
      </div>

      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Member ID</span>
          <span className={cls.metaValue}>{personal.memberId}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Partner ID</span>
          <span className={cls.metaValue}>{personal.partnerId}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>PartnerPersonal ID</span>
          <span className={cls.metaValue}>{personal.id}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Created</span>
          <span className={cls.metaValue}>
            {personal.createdAt
              ? new Date(personal.createdAt).toLocaleString()
              : "—"}
          </span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Updated</span>
          <span className={cls.metaValue}>
            {personal.updatedAt
              ? new Date(personal.updatedAt).toLocaleString()
              : "—"}
          </span>
        </div>
      </div>

      <div className={cls.layout}>
        {/* LEFT: Real Image */}
        <section className={cls.imageCard}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Real Verification Image</h2>
            <p className={cls.cardSubtitle}>
              This is the partner&apos;s private real-life verification image.
              Used only for trust & safety checks.
            </p>
          </div>

          <div className={cls.imagePreviewWrap}>
            <div className={cls.imageGlow}>
              {realImageUrl ? (
                <img
                  src={realImageUrl}
                  alt="Partner real verification"
                  className={cls.realImage}
                />
              ) : (
                <div className={cls.realImageFallback}>
                  No verification image
                </div>
              )}
            </div>
            {personal.realImage?.updatedAt && (
              <p className={cls.imageMeta}>
                Last updated{" "}
                {new Date(personal.realImage.updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className={cls.imageForm}>
            <label className={cls.fileLabel}>
              <span className={cls.fileLabelText}>Choose real image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleRealImageChange}
              />
            </label>

            {realImageFile && (
              <p className={cls.fileName}>
                Selected: <strong>{realImageFile.name}</strong>
              </p>
            )}

            <div className={cls.imageActionsRow}>
              <button
                type="button"
                className={cls.imageBtn}
                onClick={handleUploadRealImage}
                disabled={uploading || !realImageFile}
              >
                {uploading ? "Uploading…" : "Upload Real Image"}
              </button>

              <button
                type="button"
                className={cls.viewImageBtn}
                onClick={handleViewImage}
                disabled={!realImageUrl}
              >
                View Full Image
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT: Form */}
        <section className={cls.formCard}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Identity & Lifestyle Trust</h2>
            <p className={cls.cardSubtitle}>
              Names, birthday, gender, education, career, socials and loyalty
              markers to keep this partner real and responsible.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            {/* Identity */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Identity</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="firstName">
                    First name
                  </label>
                  <input
                    id="firstName"
                    className={cls.input}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Real first name"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    className={cls.input}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Real last name"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="birthday">
                    Birthday
                  </label>
                  <input
                    id="birthday"
                    type="date"
                    className={cls.input}
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="gender">
                    Gender
                  </label>
                  <select
                    id="gender"
                    className={cls.select}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDERS.map((g) => (
                      <option key={g} value={g}>
                        {genderLabel(g)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="country">
                    Country
                  </label>
                  <select
                    id="country"
                    className={cls.select}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {countryLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="city">
                    City
                  </label>
                  <input
                    id="city"
                    className={cls.input}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="address">
                    Address (private)
                  </label>
                  <input
                    id="address"
                    className={cls.input}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Street / Area (private)"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="mobilePhone">
                    Mobile phone
                  </label>
                  <input
                    id="mobilePhone"
                    className={cls.input}
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    placeholder="+1 403 ..."
                  />
                </div>
              </div>
            </div>

            {/* Work & Responsibility */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Career & Responsibility</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workPlaceName">
                    Workplace / Company
                  </label>
                  <input
                    id="workPlaceName"
                    className={cls.input}
                    value={workPlaceName}
                    onChange={(e) => setWorkPlaceName(e.target.value)}
                    placeholder="Company / Organization"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workPosition">
                    Position / Role
                  </label>
                  <input
                    id="workPosition"
                    className={cls.input}
                    value={workPosition}
                    onChange={(e) => setWorkPosition(e.target.value)}
                    placeholder="Job title"
                  />
                </div>
              </div>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="responsibleNotes">
                  Responsibility & trust notes
                </label>
                <textarea
                  id="responsibleNotes"
                  className={cls.textarea}
                  rows={3}
                  value={responsibleNotes}
                  onChange={(e) => setResponsibleNotes(e.target.value)}
                  placeholder="Notes about how responsible / reliable this partner is in the club."
                />
              </div>
            </div>

            {/* Education */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Education</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="educationLevel">
                    Education level
                  </label>
                  <select
                    id="educationLevel"
                    className={cls.select}
                    value={educationLevel}
                    onChange={(e) => setEducationLevel(e.target.value)}
                  >
                    <option value="">Select level</option>
                    {EDUCATION_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {formatLabel(level)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="schoolNote">
                    School / College notes
                  </label>
                  <textarea
                    id="schoolNote"
                    className={cls.textarea}
                    rows={2}
                    value={schoolNote}
                    onChange={(e) => setSchoolNote(e.target.value)}
                    placeholder="Any relevant school / college background."
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="universityNote">
                    University notes
                  </label>
                  <textarea
                    id="universityNote"
                    className={cls.textarea}
                    rows={2}
                    value={universityNote}
                    onChange={(e) => setUniversityNote(e.target.value)}
                    placeholder="Degree, Masters, certifications etc."
                  />
                </div>
              </div>
            </div>

            {/* Socials */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Social Profiles</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialFacebook">
                    Facebook
                  </label>
                  <input
                    id="socialFacebook"
                    className={cls.input}
                    value={socialFacebook}
                    onChange={(e) => setSocialFacebook(e.target.value)}
                    placeholder="Profile URL or handle"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialInstagram">
                    Instagram
                  </label>
                  <input
                    id="socialInstagram"
                    className={cls.input}
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(e.target.value)}
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialTikTok">
                    TikTok
                  </label>
                  <input
                    id="socialTikTok"
                    className={cls.input}
                    value={socialTikTok}
                    onChange={(e) => setSocialTikTok(e.target.value)}
                    placeholder="@username"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialYouTube">
                    YouTube
                  </label>
                  <input
                    id="socialYouTube"
                    className={cls.input}
                    value={socialYouTube}
                    onChange={(e) => setSocialYouTube(e.target.value)}
                    placeholder="Channel link"
                  />
                </div>
              </div>
            </div>

            {/* Loyalty & Priority */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Loyalty & Club Priority</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="loyalty">
                    Loyalty level
                  </label>
                  <select
                    id="loyalty"
                    className={cls.select}
                    value={loyalty}
                    onChange={(e) => setLoyalty(e.target.value)}
                  >
                    <option value="">Select loyalty</option>
                    {LOYALTY_LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {formatLabel(lvl)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="priority">
                    Club priority
                  </label>
                  <select
                    id="priority"
                    className={cls.select}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="">Select priority</option>
                    {PRIORITY_LEVELS.map((p) => (
                      <option key={p} value={p}>
                        {formatLabel(p)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="financial">
                    Financial level
                  </label>
                  <select
                    id="financial"
                    className={cls.select}
                    value={financial}
                    onChange={(e) => setFinancial(e.target.value)}
                  >
                    <option value="">Select level</option>
                    {FINANCIAL_LEVELS.map((f) => (
                      <option key={f} value={f}>
                        {formatLabel(f)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workWithUs">
                    Work with us (role)
                  </label>
                  <select
                    id="workWithUs"
                    className={cls.select}
                    value={workWithUs}
                    onChange={(e) => setWorkWithUs(e.target.value)}
                  >
                    <option value="">None</option>
                    {WORK_WITH_US_ROLES.map((w) => (
                      <option key={w} value={w}>
                        {formatLabel(w)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={loadPersonal}
                disabled={saving}
              >
                Reset / Refresh
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserMemberPartnerPersonal;
